import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";
import {
  Add as AddIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Gesture as GestureIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";

type RowActionButtonsProps = {
  onView?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onAdd?: () => void;
  onDownload?: () => void;
  onDraw?: () => void;
  onCopy?: () => void;
  copyValue?: string;
  loading?: boolean;
};

const iconButtonSx = { width: 32, height: 32 };

function ActionButton({
  title,
  color,
  disabled,
  onClick,
  children,
}: {
  title: string;
  color?: "default" | "error" | "primary";
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          size="small"
          color={color}
          disabled={disabled}
          onClick={onClick}
          aria-label={title}
          sx={iconButtonSx}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

export default function RowActionButtons({
  onView,
  onEdit,
  onDelete,
  onAdd,
  onDownload,
  onDraw,
  onCopy,
  copyValue,
  loading = false,
}: RowActionButtonsProps) {
  const { t } = useTranslation();
  const actions: ReactNode[] = [];

  if (onView) {
    actions.push(
      <ActionButton key="view" title={t("common.detail")} disabled={loading} onClick={onView}>
        <VisibilityIcon fontSize="small" />
      </ActionButton>,
    );
  }
  if (onEdit) {
    actions.push(
      <ActionButton key="edit" title={t("common.edit")} disabled={loading} onClick={onEdit}>
        <EditIcon fontSize="small" />
      </ActionButton>,
    );
  }
  if (onDelete) {
    actions.push(
      <ActionButton key="delete" title={t("common.delete")} color="error" disabled={loading} onClick={onDelete}>
        <DeleteIcon fontSize="small" />
      </ActionButton>,
    );
  }
  if (onAdd) {
    actions.push(
      <ActionButton key="add" title={t("common.create")} color="primary" disabled={loading} onClick={onAdd}>
        <AddIcon fontSize="small" />
      </ActionButton>,
    );
  }
  if (onDraw) {
    actions.push(
      <ActionButton key="draw" title={t("common.draw")} disabled={loading} onClick={onDraw}>
        <GestureIcon fontSize="small" />
      </ActionButton>,
    );
  }
  if (onDownload) {
    actions.push(
      <ActionButton key="download" title={t("common.download")} disabled={loading} onClick={onDownload}>
        <DownloadIcon fontSize="small" />
      </ActionButton>,
    );
  }
  if (onCopy) {
    actions.push(
      <ActionButton
        key="copy"
        title={copyValue ? t("common.copyValue", { value: copyValue }) : t("common.copyId")}
        onClick={onCopy}
      >
        <ContentCopyIcon fontSize="small" />
      </ActionButton>,
    );
  }

  const rows: ReactNode[] = [];
  for (let i = 0; i < actions.length; i += 3) {
    rows.push(
      <Stack
        key={`r-${i}`}
        direction="row"
        spacing={0.25}
        sx={{ mb: i + 3 < actions.length ? 0.5 : 0 }}
      >
        {actions.slice(i, i + 3)}
      </Stack>,
    );
  }

  return <Box sx={{ py: 0.5 }}>{rows}</Box>;
}
